<template>
  <v-expansion-panel
    ref="container"
    class="invited-signatories-expansion-panel"
    :class="{ 'disable-animation': isDashBoardRoute }"
    focusable
    @click="scroll"
  >
    <v-expansion-panel-title
      :class="{
        'drawer-subhead panel-title': !isDashBoardRoute,
        'panel-title-font': isDashBoardRoute
      }"
      data-test-id="invited-signatories-expansion-panel"
      @click="scroll"
    >
      {{
        hasApprovers
          ? i18n.t('titles.invited_persons')
          : i18n.t('titles.invited_signatories')
      }}
      <template #actions="{ expanded }">
        <VIcon :class="{ 'fa-rotate-180': expanded }" :size="12">
          far fa-chevron-down
        </VIcon>
      </template>
    </v-expansion-panel-title>
    <!-- PLAIN (single group → separate signees) -->
    <v-expansion-panel-text
      v-if="documentDetails && !hasGroupedSequence"
      class="text-wrapper"
      data-test-id="signees-unordered"
    >
      <div
        class="d-flex flex-column"
        :class="{
          'ga-4': isDashBoardRoute,
          'ga-6': !isDashBoardRoute
        }"
      >
        <template v-for="signee in flatSignees" :key="signee.signeeId">
          <InvitedSignee
            :document-id="documentDetails?.documentId"
            :email-element-width="emailElementWidth"
            :is-dash-board="isDashBoardRoute"
            :is-unordered="true"
            :signature="signee"
          />
        </template>
      </div>
    </v-expansion-panel-text>
    <!-- /PLAIN -->

    <!-- ORDERED (2+ groups) -->
    <v-expansion-panel-text
      v-else-if="documentDetails"
      ref="container"
      data-test-id="signees-ordered"
      style="padding-left: 0 !important"
    >
      <div
        class="d-flex flex-column"
        :class="{
          'ga-4': isDashBoardRoute,
          'ga-6': !isDashBoardRoute
        }"
      >
        <template v-for="renderGroup in orderedGroups" :key="renderGroup.key">
          <!-- Quorum header: only when a partial quorum is set for the group.
               With 2+ groups it carries the order number and members below
               are indented with the spacer. A lone group keeps the text. -->
          <div
            v-if="renderGroup.quorumText"
            class="d-flex align-center"
            :class="{
              'ga-2': isDashBoardRoute,
              'ga-4': !isDashBoardRoute
            }"
            :data-test-id="`signer-${renderGroup.order}`"
          >
            <div
              v-if="showGroupOrder"
              class="signee-order d-flex align-center justify-center flex-shrink-0"
              :class="{
                'dashboard-view': isDashBoardRoute
              }"
              :data-test-id="`signers-order-${renderGroup.order}`"
            >
              {{ renderGroup.order }}
            </div>
            <div
              class="quorum-header text-medium-emphasis flex-grow-1 min-w-0"
              :class="{
                'dashboard-view': isDashBoardRoute
              }"
              data-test-id="quorum-required"
            >
              {{ renderGroup.quorumText }}
            </div>
          </div>

          <div
            v-for="(signee, signeeIndex) in renderGroup.signees"
            :key="signee.signeeId"
            class="d-flex align-center"
            :class="{
              'ga-2': isDashBoardRoute,
              'ga-4': !isDashBoardRoute
            }"
            :data-test-id="
              !renderGroup.quorumText && signeeIndex === 0
                ? `signer-${renderGroup.order}`
                : undefined
            "
          >
            <div
              v-if="
                showGroupOrder && signeeIndex === 0 && !renderGroup.quorumText
              "
              class="signee-order d-flex align-center justify-center flex-shrink-0"
              :class="{
                'dashboard-view': isDashBoardRoute
              }"
              :data-test-id="`signers-order-${renderGroup.order}`"
            >
              {{ renderGroup.order }}
            </div>
            <div
              v-else-if="showGroupOrder"
              class="signee-order-spacer flex-shrink-0"
              :class="{
                'dashboard-view': isDashBoardRoute
              }"
            />

            <div class="flex-grow-1 min-w-0">
              <InvitedSignee
                :document-id="documentDetails?.documentId"
                :email-element-width="emailElementWidth"
                :has-approvers="hasApprovers"
                :is-dash-board="isDashBoardRoute"
                :is-unordered="false"
                :signature="signee"
              />
            </div>
          </div>
        </template>
      </div>
    </v-expansion-panel-text>
    <!-- /ORDERED -->
  </v-expansion-panel>
</template>

<script setup lang="ts">
import InvitedSignee from '@/components/signatureArea/InvitedSignee.vue';
import { useI18n } from 'vue-i18n';
import { useDocumentContext } from '@/composables/useDocumentContext';
import { SigneeType } from '@/types/enums/SigneeType.ts';
import { computed, type PropType, ref } from 'vue';
import type { Signee } from '@/types/Signee.ts';
import type { SigneeRenderGroup } from '@/types/SigneeRenderGroup.ts';
import type { Document } from '@/types/Document.ts';
import { useRoute } from 'vue-router';
import { useResizeObserver } from '@vueuse/core';

const i18n = useI18n();
const route = useRoute();
const { currentDocument } = useDocumentContext();

const props = defineProps({
  document: {
    type: Object as PropType<Document | null>,
    default: null
  }
});

const container = ref<HTMLElement>();
const emailElementWidth = ref(0);

const documentDetails = computed(() =>
  props.document ? props.document : currentDocument.value
);

const isDashBoardRoute = computed(() => route.name === 'dashboard');

// Envelopes carry the grouping + quorum on `signeeGroups`; plain documents only
// have `signeesOrdered` (and never a quorum). The frontend distinguishes them by
// whether the document has files.
const isEnvelope = computed(
  () => (documentDetails.value?.files?.length ?? 0) > 0
);

// The grouped view model — sourced from `signeeGroups` for envelopes,
// `signeesOrdered` otherwise. A quorum header is attached when this group has
// 2+ members and the quorum is a definite, positive number (a `null` quorum
// means everyone signs anyway and adds nothing). A lone group can carry a
// quorum too (see `canShowQuorumSection` in OrderedInvitees.vue); the header
// is shown without the group-order badge.
const orderedGroups = computed<SigneeRenderGroup[]>(() => {
  const doc = documentDetails.value;
  if (!doc) return [];

  const source = isEnvelope.value
    ? [...(doc.signeeGroups ?? [])]
        .sort((a, b) => (a.signOrder ?? 0) - (b.signOrder ?? 0))
        .map((group) => ({
          signees: group.signees ?? [],
          requiredSignatures: group.requiredSignatures ?? null,
          requiredApprovals: group.requiredApprovals ?? null
        }))
    : doc.signeesOrdered.map((signees) => ({
        signees,
        requiredSignatures: null,
        requiredApprovals: null
      }));

  const renderable = source.filter((group) => group.signees.length > 0);

  return renderable.map((group, index): SigneeRenderGroup => {
    const isApprover = group.signees[0]?.signeeType === SigneeType.APPROVE;
    const required = isApprover
      ? group.requiredApprovals
      : group.requiredSignatures;
    const total = group.signees.length;
    const hasQuorum = total > 1 && required != null && required >= 1;
    return {
      key: index,
      order: index + 1,
      signees: group.signees,
      quorumText: hasQuorum
        ? i18n.t(
            isApprover
              ? 'texts.quorum_invites_need_to_approve'
              : 'texts.quorum_invites_need_to_sign',
            { required, total },
            required ?? 1
          )
        : null
    };
  });
});

// A real ordered sequence needs 2+ groups; a single group (however it was made)
// is shown as plain, separate signees. The exception is a lone group that
// carries a quorum: its header only exists in this rendering, so that view is
// used to surface the sentence — without the group-order badge.
const hasGroupedSequence = computed(
  () =>
    orderedGroups.value.length >= 2 ||
    orderedGroups.value.some((group) => group.quorumText !== null)
);

// Numbered badges and the indent spacer belong to a real sequence (2+ groups).
// A lone quorum group keeps the header text but not the order column.
const showGroupOrder = computed(() => orderedGroups.value.length >= 2);

// Flat signee list from the active source — drives the plain single-group view
// and the panel title.
const flatSignees = computed<Signee[]>(() =>
  orderedGroups.value.flatMap((group) => group.signees)
);

const hasApprovers = computed(() =>
  flatSignees.value.some((invitee) => invitee.signeeType === SigneeType.APPROVE)
);

function scroll() {
  setTimeout(() => {
    const documentInfo = document.getElementById('document-info');
    if (documentInfo) {
      documentInfo.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
        behavior: 'smooth'
      });
    }
  }, 200);
}

useResizeObserver(container, (entries) => {
  const [entry] = entries;
  if (!entry) return;
  const { width } = entry.contentRect;
  const isOrdered = showGroupOrder.value;
  let subtraction = 40;
  if (isOrdered && isDashBoardRoute.value) {
    subtraction = 76;
  }
  if (isOrdered && !isDashBoardRoute.value) {
    subtraction = 116;
  }

  emailElementWidth.value = width - subtraction;
});
</script>

<style scoped lang="scss">
.invited-signatories-expansion-panel
  :deep(.v-expansion-panel-title--focusable) {
  &:focus-visible {
    box-shadow:
      rgb(255, 255, 255) 0 0 0 0,
      rgba(59, 130, 246, 0.5) 0 0 0 0.5px,
      rgba(0, 0, 0, 0) 0 0 0 0;
  }
}

.signee-order {
  flex-shrink: 0;
  box-sizing: border-box;
  width: 30px;
  height: 30px;
  font-size: 16px;
  line-height: 1;
  font-weight: 500;
  color: rgb(var(--v-theme-primary));
  border-radius: 50%;
  background-color: color-mix(in srgb, rgb(var(--v-theme-primary)) 12%, white);

  &.dashboard-view {
    min-height: 20px;
    min-width: 20px;
    width: 20px;
    height: 20px;
    font-size: 13px;
    line-height: 1;
    font-weight: 500;
  }
}

.signee-order-spacer {
  flex-shrink: 0;
  width: 30px;

  &.dashboard-view {
    min-width: 20px;
    width: 20px;
  }
}

.quorum-header {
  line-height: 1.25;

  &.dashboard-view {
    font-size: 13px;
  }
}

:deep(
  .v-expansion-panel-title--focusable.v-expansion-panel-title--active
    .v-expansion-panel-title__overlay
) {
  opacity: 0 !important;
}

:deep(
  .v-expansion-panel-title:hover > .v-expansion-panel-title__overlay,
  .v-expansion-panel-title > .v-expansion-panel-title__overlay
) {
  opacity: 0 !important;
}

.panel-title {
  min-height: 49px !important;
}

.panel-title-font {
  padding-top: 14px;
  padding-bottom: 0 !important;
}

.text-wrapper :deep(.v-expansion-panel-text__wrapper) {
  padding-top: 4px;
}

.invited-signatories-expansion-panel.disable-animation > * {
  transition: none !important;
}
</style>
